<script lang="ts">
  import { fade, scale } from 'svelte/transition';
  import type { EditorGroup, LinkFormValues } from '../types';

  let {
    open = false,
    mode = 'create',
    groups = [],
    values,
    saving = false,
    error = '',
    onClose,
    onSubmit,
  }: {
    open: boolean;
    mode?: 'create' | 'edit';
    groups?: EditorGroup[];
    values: LinkFormValues;
    saving?: boolean;
    error?: string;
    onClose?: () => void;
    onSubmit?: () => void;
  } = $props();

  function close() {
    onClose?.();
  }

  function handleBackdropClick() {
    close();
  }

  function handleDialogClick(event: MouseEvent) {
    event.stopPropagation();
  }

  function handleDialogKeydown(event: KeyboardEvent) {
    event.stopPropagation();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  }

  // When the user picks the Calendar embed type and hasn't set an emoji
  // yet, seed a sensible default so the public profile can recognise the
  // link without the user having to remember to paste a 📅 glyph. We
  // only overwrite when the field is empty — never stomp explicit choices.
  const EMBED_DEFAULT_EMOJIS: Record<string, string> = {
    calendar: '📅',
    youtube: '▶️',
    spotify: '🎵',
    map: '📍',
    document: '📄',
  };

  function handleEmbedTypeChange(event: Event) {
    const next = (event.currentTarget as HTMLSelectElement).value;
    values.embed_type = next;
    const hinted = EMBED_DEFAULT_EMOJIS[next];
    if (hinted && !values.emoji) values.emoji = hinted;
  }
</script>

{#if open}
  <div transition:fade={{ duration: 180 }} class="fixed inset-0 z-50 flex items-center justify-center p-4" onkeydown={handleKeydown} role="presentation">
    <button
      type="button"
      class="absolute inset-0 bg-gray-950/70 backdrop-blur-md hover:opacity-100"
      aria-label="Close dialog"
      onclick={handleBackdropClick}
    ></button>
    <div transition:scale={{ duration: 180, start: 0.96, opacity: 0 }} class="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-label="Link editor" tabindex="-1" onclick={handleDialogClick} onkeydown={handleDialogKeydown}>
      <div class="flex items-start justify-between gap-4">
        <div>
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-sky-600">링크</div>
          <h3 class="mt-2 text-2xl font-semibold text-gray-950">{mode === 'create' ? '링크 추가' : '링크 편집'}</h3>
        </div>
        <button type="button" class="rounded-full border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50" onclick={close}>
          닫기
        </button>
      </div>

      {#if error}
        <div class="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      {/if}

      <div class="mt-6 grid gap-4 md:grid-cols-2">
        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">제목</span>
          <input bind:value={values.title} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">이모지</span>
          <input bind:value={values.emoji} maxlength="10" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block md:col-span-2">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">URL</span>
          <input bind:value={values.url} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block md:col-span-2">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">설명</span>
          <textarea bind:value={values.description} rows="3" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400"></textarea>
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">유형</span>
          <select bind:value={values.link_type} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400">
            <option value="external">외부 링크</option>
            <option value="post">게시물</option>
            <option value="file">파일</option>
          </select>
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">공개 범위</span>
          <select bind:value={values.visibility} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400">
            <option value="public">전체 공개</option>
            <option value="internal">내부 공개</option>
          </select>
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">그룹</span>
          <select bind:value={values.link_group_id} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400">
            <option value="">미분류</option>
            {#each groups as group}
              <option value={group.id}>{group.emoji ? `${group.emoji} ${group.title}` : group.title}</option>
            {/each}
          </select>
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">임베드</span>
          <select value={values.embed_type} onchange={handleEmbedTypeChange} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400">
            <option value="">없음</option>
            <option value="youtube">유튜브</option>
            <option value="document">문서</option>
            <option value="spotify">스포티파이</option>
            <option value="map">지도</option>
            <option value="calendar">캘린더</option>
          </select>
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">예약 시작일</span>
          <input bind:value={values.scheduled_at} type="datetime-local" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">만료일</span>
          <input bind:value={values.expires_at} type="datetime-local" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block md:col-span-2">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">썸네일 URL</span>
          <input bind:value={values.thumbnail_url} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">UTM 소스</span>
          <input bind:value={values.utm_source} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">UTM 매체</span>
          <input bind:value={values.utm_medium} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="block md:col-span-2">
          <span class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">UTM 캠페인</span>
          <input bind:value={values.utm_campaign} class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none ring-0 transition focus:border-gray-400" />
        </label>

        <label class="flex items-center gap-3 md:col-span-2">
          <input bind:checked={values.is_active} type="checkbox" class="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
          <span class="text-sm text-gray-700">링크 활성화</span>
        </label>
      </div>

      <div class="mt-6 flex justify-end gap-3">
        <button type="button" class="rounded-full border border-gray-200 px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50" onclick={close}>
          취소
        </button>
        <button type="button" class="rounded-full bg-gray-950 px-5 py-3 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60" disabled={saving} onclick={() => onSubmit?.()}>
          {saving ? '저장 중...' : mode === 'create' ? '링크 추가' : '변경 저장'}
        </button>
      </div>
    </div>
  </div>
{/if}
